import React from 'react'
import { Tooltip } from 'antd'
import styled from 'styled-components'
import { DataType, ProcessProgress } from './interface'

const COMPLETED_COLOR = '#389e0d'
const PENDING_COLOR = '#d9d9d9'
const ISSUE_COLOR = '#cf1322'

const ProgressWrapper = styled.div`
  display: inline-flex;
  min-width: 210px;
  flex-direction: column;
  gap: 3px;
  vertical-align: middle;
`

const ProcessRow = styled.div`
  display: grid;
  min-height: 18px;
  grid-template-columns: 58px 54px minmax(0, 1fr);
  align-items: center;
  column-gap: 6px;
`

const ProcessLabel = styled.span`
  overflow: hidden;
  color: #595959;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StageTrack = styled.span`
  display: flex;
  width: 54px;
  align-items: center;
`

const StageConnector = styled.span<{ $completed: boolean }>`
  height: 2px;
  flex: 1;
  background: ${({ $completed }) => ($completed ? COMPLETED_COLOR : PENDING_COLOR)};
  transition: background-color 0.2s;
`

const StageDot = styled.span<{ $completed: boolean; $current: boolean }>`
  width: 9px;
  height: 9px;
  flex: 0 0 9px;
  border: 2px solid ${({ $completed }) => ($completed ? COMPLETED_COLOR : PENDING_COLOR)};
  border-radius: 50%;
  background: ${({ $completed }) => ($completed ? COMPLETED_COLOR : '#fff')};
  box-shadow: ${({ $current }) => ($current ? '0 0 0 2px rgba(56, 158, 13, 0.18)' : 'none')};
  transition: border-color 0.2s, background-color 0.2s, box-shadow 0.2s;
`

const CurrentStatus = styled.span<{ $muted: boolean; $issue: boolean }>`
  overflow: hidden;
  color: ${({ $issue, $muted }) => ($issue ? ISSUE_COLOR : $muted ? '#8c8c8c' : '#595959')};
  font-size: 12px;
  line-height: 18px;
  text-overflow: ellipsis;
  white-space: nowrap;
`

interface Milestone {
  code: string
  label: string
}

interface ProcessConfig {
  key: 'measurement' | 'delivery' | 'montage'
  label: string
  stageOrder: string[]
  milestones: Milestone[]
  statuses: Record<string, string>
  cancelledStatus: string
  issueStages?: string[]
}

const PROCESS_CONFIGS: ProcessConfig[] = [
  {
    key: 'measurement',
    label: 'Замер',
    stageOrder: ['new', 'work', 'measurer-arrived', 'measurement-ready', 'contract-signed', 'done'],
    milestones: [
      { code: 'work', label: 'Взят в работу' },
      { code: 'measurement-ready', label: 'Замер произведён' },
      { code: 'done', label: 'Замер завершён' },
    ],
    statuses: {
      new: 'Ожидает',
      work: 'В работе',
      'measurer-arrived': 'Замерщик прибыл',
      'measurement-ready': 'Замер готов',
      'contract-signed': 'Договор подписан',
      done: 'Готово',
    },
    cancelledStatus: 'Отменён',
  },
  {
    key: 'delivery',
    label: 'Доставка',
    stageOrder: [
      'new',
      'sent_to_schedex',
      'uploaded_from_schedex',
      'planned',
      'exported_to_wd',
      'ready_routes',
      'done',
    ],
    milestones: [
      { code: 'exported_to_wd', label: 'Маршрут назначен' },
      { code: 'ready_routes', label: 'На доставке' },
      { code: 'done', label: 'Доставка выполнена' },
    ],
    statuses: {
      new: 'Ожидает',
      sent_to_schedex: 'Планируется',
      uploaded_from_schedex: 'Планируется',
      planned: 'Запланирована',
      exported_to_wd: 'Маршрут назначен',
      ready_routes: 'На доставке',
      done: 'Выполнена',
    },
    cancelledStatus: 'Отменена',
  },
  {
    key: 'montage',
    label: 'Монтаж',
    stageOrder: ['new', 'work', 'installed', 'accepted', 'not_accepted', 'done'],
    milestones: [
      { code: 'work', label: 'Взят в работу' },
      { code: 'installed', label: 'Монтаж произведён' },
      { code: 'done', label: 'Монтаж выполнен' },
    ],
    statuses: {
      new: 'Ожидает',
      work: 'В работе',
      installed: 'Установлено',
      accepted: 'Принято',
      not_accepted: 'Не принято',
      done: 'Выполнен',
    },
    cancelledStatus: 'Отменён',
    issueStages: ['not_accepted'],
  },
]

const getReachedStageIndex = (progress: ProcessProgress, stageOrder: string[]): number => {
  const historyIndexes = (progress?.history || []).map(stage => stageOrder.indexOf(stage))
  const currentStageIndex =
    progress?.stage && progress.stage !== 'cancelled' ? stageOrder.indexOf(progress.stage) : -1

  return Math.max(currentStageIndex, ...historyIndexes, -1)
}

const getProgress = (row: DataType, key: ProcessConfig['key']): ProcessProgress => {
  return row[`${key}_progress`] || { stage: null, history: [] }
}

const getStatus = (progress: ProcessProgress, process: ProcessConfig): string => {
  if (!progress.stage) return 'Нет карточки'
  if (progress.stage === 'cancelled') return process.cancelledStatus
  return process.statuses[progress.stage] || 'В процессе'
}

export const OrderProcessProgress = ({ row }: { row: DataType }) => {
  return (
    <ProgressWrapper>
      {PROCESS_CONFIGS.map(process => {
        const progress = getProgress(row, process.key)
        const reachedStageIndex = getReachedStageIndex(progress, process.stageOrder)
        const reachedMilestoneIndex = process.milestones.reduce((result, milestone, index) => {
          return reachedStageIndex >= process.stageOrder.indexOf(milestone.code) ? index : result
        }, -1)
        const status = getStatus(progress, process)
        const isCancelled = progress.stage === 'cancelled'
        const isIssue = isCancelled || Boolean(process.issueStages?.includes(progress.stage || ''))
        const isMuted = !progress.stage || progress.stage === 'new'

        return (
          <ProcessRow key={process.key} aria-label={`${process.label}: ${status}`}>
            <ProcessLabel>{process.label}</ProcessLabel>
            <StageTrack>
              {process.milestones.map((milestone, index) => {
                const completed = reachedStageIndex >= process.stageOrder.indexOf(milestone.code)
                const current =
                  completed &&
                  index === reachedMilestoneIndex &&
                  progress.stage !== 'done' &&
                  !isCancelled

                return (
                  <React.Fragment key={milestone.code}>
                    {index > 0 && <StageConnector $completed={completed} />}
                    <Tooltip title={`${process.label}: ${milestone.label}`}>
                      <StageDot $completed={completed} $current={current} />
                    </Tooltip>
                  </React.Fragment>
                )
              })}
            </StageTrack>
            <Tooltip title={`${process.label}: ${status}`}>
              <CurrentStatus $muted={isMuted} $issue={isIssue}>
                {status}
              </CurrentStatus>
            </Tooltip>
          </ProcessRow>
        )
      })}
    </ProgressWrapper>
  )
}
